import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { Alert, showError, showSuccess } from '../lib/alerts';
import Swal from 'sweetalert2';

interface ProfilePageProps {
  session: Session | null;
  supabase: SupabaseClient;
}

export default function ProfilePage({ session, supabase }: ProfilePageProps) {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState(session?.user.user_metadata?.full_name || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!session) return;
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: fullName },
      });
      if (error) throw error;
      
      const { error: dbError } = await supabase
        .from('users')
        .update({ full_name: fullName })
        .eq('id', session.user.id);
        
      if (dbError) throw dbError;

      showSuccess('Profile updated successfully');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteAccount() {
    try {
      const result = await Alert.fire({
        icon: 'warning',
        title: 'Delete account?',
        html: 'This action cannot be undone. Your account will be completely wiped.<br><br>Please type <b>DELETE</b> to confirm.',
        input: 'text',
        inputPlaceholder: 'DELETE',
        showCancelButton: true,
        confirmButtonText: 'Delete my account',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#ff4d6d',
        preConfirm: (inputValue) => {
          if (inputValue !== 'DELETE') {
            Swal.showValidationMessage('You must type DELETE to confirm');
          }
          return inputValue === 'DELETE';
        }
      });

      if (!result.isConfirmed) {
        return;
      }

      setDeleting(true);

      const { error: functionError } = await supabase.rpc('delete_my_account');
      if (functionError) {
        throw functionError;
      }

      await supabase.auth.signOut();
      showSuccess('Account deleted successfully');
      navigate('/login', { replace: true });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <main className="dashboard-content">
        <section className="stack-block">
          <div className="section-heading-row section-heading-row--spread">
            <div>
              <div className="section-kicker">PROFILE</div>
              <h2>Account Settings</h2>
            </div>
          </div>

          <div style={{ maxWidth: '600px', backgroundColor: 'var(--bg-secondary)', padding: '2rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
            <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>Update your personal information or manage your account.</p>

            <form onSubmit={handleSaveProfile}>
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="fullName" style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  className="text-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  style={{ width: '100%', padding: '0.75rem', backgroundColor: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'var(--text-primary)' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Email Address</label>
                <input
                  type="email"
                  className="text-input"
                  value={session?.user.email || ''}
                  disabled
                  style={{ width: '100%', padding: '0.75rem', backgroundColor: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'var(--text-muted)', opacity: 0.7, cursor: 'not-allowed' }}
                />
              </div>
              <button type="submit" className="button button--primary" disabled={saving || deleting} style={{ width: '100%' }}>
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </form>

            <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <h3 style={{ color: 'var(--danger)', marginBottom: '1rem' }}>Danger Zone</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                Once you delete your account, there is no going back. Please be certain.
              </p>
              <button 
                type="button" 
                className="button button--ghost button--danger" 
                onClick={handleDeleteAccount} 
                disabled={saving || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
